import { Row } from 'antd'
import type * as React from 'react'
import type { ReactElement } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import './HeaderSection.less'
import { BackButton } from '../../general'

export interface HeaderSectionProps {
  backLink?: boolean
  rightChildren?: ReactElement
  subtitle?: string
  iconColor?: string
  iconContent?: ReactElement
  titleSubString?: string
  title?: string | ReactElement
  style?: React.CSSProperties
  headingStyle?: React.CSSProperties
  subtitleStyle?: React.CSSProperties
  onClick?: () => void
  flexColumn?: boolean
  testId?: string
}

export function HeaderSection({
  backLink,
  rightChildren,
  subtitle,
  iconColor,
  iconContent,
  title,
  style,
  onClick,
  flexColumn,
  headingStyle,
  subtitleStyle,
  testId
}: HeaderSectionProps) {
  const topRowJustify = backLink
    ? rightChildren
      ? 'space-between'
      : 'start'
    : rightChildren
      ? 'end'
      : 'start'

  const isMobile = !isBrowser && !isTablet
  return (
    <div style={style} data-testid={testId || 'header-section'}>
      {backLink && (
        <Row
          justify={topRowJustify}
          style={styles.topRow}
          data-testid={
            testId ? `${testId}-back-row` : 'header-section-back-row'
          }
        >
          <div
            data-testid={
              testId
                ? `${testId}-back-button-container`
                : 'header-section-back-button-container'
            }
          >
            <BackButton onClick={onClick} />
          </div>
        </Row>
      )}
      <Row>
        <div
          style={{
            ...styles.headingContainer,
            ...(headingStyle && headingStyle),
            ...(flexColumn && { flexDirection: 'column' })
          }}
          data-testid={
            testId
              ? `${testId}-heading-container`
              : 'header-section-heading-container'
          }
        >
          <div
            style={styles.titleContainer}
            data-testid={
              testId
                ? `${testId}-title-container`
                : 'header-section-title-container'
            }
          >
            {iconContent && (
              <div
                style={{ background: iconColor, ...styles.iconStyle }}
                data-testid={testId ? `${testId}-icon` : 'header-section-icon'}
              >
                {iconContent}
              </div>
            )}
            <div
              data-testid={
                testId
                  ? `${testId}-text-content`
                  : 'header-section-text-content'
              }
            >
              <h2
                style={styles.heading}
                data-testid={
                  testId ? `${testId}-title` : 'header-section-title'
                }
              >
                {title}
              </h2>
              <p
                style={{ ...styles.subtitle, ...subtitleStyle }}
                data-testid={
                  testId ? `${testId}-subtitle` : 'header-section-subtitle'
                }
              >
                {subtitle && subtitle}
              </p>
            </div>
          </div>
          {rightChildren && (
            <div
              style={
                flexColumn
                  ? { marginTop: 5 }
                  : { paddingTop: isMobile ? 16 : 0 }
              }
              data-testid={
                testId
                  ? `${testId}-right-children`
                  : 'header-section-right-children'
              }
            >
              {rightChildren}
            </div>
          )}
        </div>
      </Row>
    </div>
  )
}

const styles = {
  backLink: {
    fontWeight: 400,
    fontSize: 14
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row' as any,
    alignItems: 'center'
  },
  divider: {
    height: 2,
    marginBottom: 0,
    width: '100%'
  },
  heading: {
    marginBottom: 0,
    textOverflow: 'ellipsis',
    hyphens: 'auto' as const,
    marginRight: 20,
    fontWeight: 700,
    fontSize: 32
  },
  headingContainer: {
    display: 'flex',
    flexWrap: 'wrap' as any,
    flex: 1,
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 22px',
    background: 'white',
    borderRadius: 15,
    marginBottom: 10,
    minHeight: 72
  },
  subtitle: {
    margin: 0,
    opacity: 0.7
  },
  topRow: {
    alignItems: 'flex-start',
    height: 52,
    marginTop: 18
  },
  iconStyle: {
    height: 38,
    width: 38,
    borderRadius: 6,
    marginRight: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
}

export default HeaderSection
